import React, { useEffect, useState } from "react";
import "../../Styles/PC/PCUsers.css";
import { useNavigate } from "react-router-dom";
import { LinearProgress } from "@mui/material";
import { getAllUsersAPI } from "../../services/pcConfigService";
import { Mail } from "@mui/icons-material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PhoneIcon from "@mui/icons-material/Phone";

const PHASE_LABELS = {
    1: "Foundation",
    2: "Optimization",
    3: "Maintenance",
    4: "Mastery",
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const ProgrammeUsers = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [phaseFilter, setPhaseFilter] = useState("All");
    const [pageNo, setPageNo] = useState(0);
    const [pageSize] = useState(10);
    const [allUsersData, setAllUsersData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        fetchAllUsers();
    }, [searchQuery, phaseFilter, pageNo]);

    const fetchAllUsers = async () => {
        try {
            const response = await getAllUsersAPI(
                searchQuery,
                pageNo,
                pageSize,
                phaseFilter
            );

            if (response?.status === "success") {
                setAllUsersData(response?.subscriptions || []);
                setTotalCount(response?.totalCount || 0);
            } else {
                setAllUsersData([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error("Error fetching all users:", error);
        }
    };

    // Reset to first page when search/filter changes
    useEffect(() => {
        setPageNo(0);
    }, [searchQuery, phaseFilter]);

    const totalPages = Math.ceil(totalCount / pageSize);

    const getInitials = (name) =>
        name
            ?.split(" ")
            ?.map((n) => n[0])
            ?.join("")
            ?.toUpperCase();

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setPageNo(page);
        }
    };

    const renderPageNumbers = () => {
        const pages = [];

        for (let i = 0; i < totalPages; i++) {
            pages.push(
                <button
                    key={i}
                    className={`pagination-number ${pageNo === i ? "active" : ""
                        }`}
                    onClick={() => handlePageChange(i)}
                >
                    {i + 1}
                </button>
            );
        }

        return pages;
    };

    const getUserSubscriptionDetails = (patientId) => {
        console.log(patientId);
        sessionStorage.setItem('selectedPatientId', patientId);
        navigate('/PC/ProgramTimeline');
    };

    return (
        <div className="page-container">
            {/* Filters */}
            <div className="filters-container">
                <div className="search-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pcusers-search-input"
                    />
                </div>

                <select
                    className="phase-select"
                    value={phaseFilter}
                    onChange={(e) => setPhaseFilter(e.target.value)}
                >
                    <option value="All">All Phases</option>
                    <option value="1">Phase 1: Foundation</option>
                    <option value="2">Phase 2: Optimization</option>
                    <option value="3">Phase 3: Maintenance</option>
                    <option value="4">Phase 4: Mastery</option>
                </select>
            </div>

            {/* Users List */}
            <div className="users-container">
                {allUsersData.length > 0 ? (
                    allUsersData.map((user) => (
                        <div className="user-progress-card" key={user._id}>
                            <div className="card-top">
                                <div className="left-section">
                                    <div className="user-avatar">
                                        {getInitials(
                                            `${user?.patientInfo?.firstName || ""} ${user?.patientInfo?.lastName || ""
                                            }`
                                        )}
                                    </div>

                                    <div>
                                        <div className="name-row">
                                            <h3 className="user-name">
                                                {user?.patientInfo?.firstName}{" "}
                                                {user?.patientInfo?.lastName}
                                            </h3>
                                            <span className="phase-badge">
                                                Phase {user.currentPhase}:{" "}
                                                {PHASE_LABELS[user.currentPhase]}
                                            </span>
                                        </div>

                                        <div className="meta-row">
                                            <Mail fontSize="small" />
                                            <span className="email-value">
                                                {user?.patientInfo?.email}
                                            </span>

                                            <PhoneIcon fontSize="small" />
                                            <span className="phone-value">
                                                {user?.patientInfo?.phoneNumber}
                                            </span>

                                            <DateRangeIcon fontSize="small" />
                                            <span className="date-value">
                                                Enrolled{" "}
                                                {formatDate(
                                                    user?.programEnrolledDate
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="arrow" onClick={() => getUserSubscriptionDetails(user?.patientId)}>›</div>
                            </div>

                            <div className="progress-section">
                                <div className="progress-header">
                                    <span>Overall Progress</span>
                                    <span className="progress-percentage">
                                        {user.progressPercentage}%
                                    </span>
                                </div>

                                <LinearProgress
                                    variant="determinate"
                                    value={user.progressPercentage || 0}
                                    className="mui-progress"
                                />
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-state">
                        No users found matching your criteria
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="pagination-container">
                    <button
                        disabled={pageNo === 0}
                        onClick={() => handlePageChange(pageNo - 1)}
                        className="pagination-btn"
                    >
                        Previous
                    </button>

                    {renderPageNumbers()}

                    <button
                        disabled={pageNo + 1 >= totalPages}
                        onClick={() => handlePageChange(pageNo + 1)}
                        className="pagination-btn"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProgrammeUsers;
